import { EuiLink } from "@inscopix/ideas-eui";
import assert from "assert";
import { NotificationsFeedBodyQuery } from "graphql/_Types";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { isNonNull } from "utils/isNonNull";
import { NotificationAlert } from "./NotificationAlert";
import { useCallback } from "react";
import { isUndefined } from "lodash";
import { RegionBadge } from "components/RegionBadge/RegionBadge";
import { useFlyoutContext } from "providers/FlyoutProvider/FlyoutProvider";
import { ExternalRegion } from "ideas.env";

interface NotificationAlertProjectSharedProps {
  data: NonNullable<
    NotificationsFeedBodyQuery["notifications"]
  >["nodes"][number] & { region?: ExternalRegion };
  onNotificationRead: (
    notificationId: string,
    region?: ExternalRegion,
    refetchNotifications?: () => void,
  ) => void;
  refetchNotifications: () => void;
}

/** Component that renders a notification that a project was shared with the user */
export const NotificationAlertProjectShared = ({
  data,
  onNotificationRead,
  refetchNotifications,
}: NotificationAlertProjectSharedProps) => {
  const { closeFlyout } = useFlyoutContext();

  const { dateCreated, hasSeen, id: notificationId } = data;
  const attrs = data.projectSharedNotification;
  assert(isNonNull(attrs));
  const { project, sharer } = attrs;

  const tenantKey = project?.tenant?.key;
  assert(tenantKey !== undefined);
  // bail out below if project is null
  // Noting this as another example of why I prefer notification props to not be raw query data
  // but instead components that are only rendered if they have all the data they need (checked by parent)
  const projectKey = project?.key ?? "";

  const projectPath = useRouteMapContext(
    (s) => s.routeMap.PROJECT.dynamicPath({ projectKey, tenantKey }).path,
  );

  const onNotificationReadMemoized = useCallback(() => {
    if (!hasSeen) {
      onNotificationRead(notificationId, data.region, refetchNotifications);
    }
  }, [
    hasSeen,
    notificationId,
    onNotificationRead,
    refetchNotifications,
    data.region,
  ]);

  if (project === null) {
    // bail out if we don't have access to this project
    return null;
  }

  const action = (() => {
    const handleClick = (close = true) => {
      close && closeFlyout();
      onNotificationReadMemoized();
    };

    return isUndefined(data.region) ? (
      <EuiLink to={projectPath} onClick={() => handleClick()}>
        Go to project
      </EuiLink>
    ) : (
      <EuiLink
        href={`${data.region.URL}${projectPath}`}
        target="_blank"
        onClick={() => handleClick(false)}
      >
        Go to project <RegionBadge regionShortName={data.region.shortName} />
      </EuiLink>
    );
  })();

  return (
    <NotificationAlert
      dateCreated={dateCreated}
      hasSeen={hasSeen}
      title="Project shared"
      onNotificationRead={onNotificationReadMemoized}
      text={
        <span>
          You have been granted access to <strong>{project.name}</strong>{" "}
          {sharer !== null ? (
            <>
              by{" "}
              <strong>
                {sharer.firstName} {sharer.lastName}
              </strong>
            </>
          ) : null}
          .
        </span>
      }
      action={action}
    />
  );
};
